import {StoreRepository, StoreListFilters} from './StoreRepository';
import {StoreAPIDataSource} from '../api/StoreAPIDataSource';
import {ProviderStoreAPIDataSource} from '../api/ProviderStoreAPIDataSource';
import {Store} from '../models/store.model';
import {StoreStatistics} from '../models/store.model';
import {mapStoreDTOToModel, mapStoreModelToDTO, mapStoreStatisticsDTOToModel} from '../mappers/store.mapper';
import {AppError} from '../../../Data/config/AppError';
import {Bounds} from '../../../Presentation/components/common/types';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

export class StoreRepositoryImpl implements StoreRepository {
  private readonly storeDataSource: typeof StoreAPIDataSource;

  constructor () {
    this.storeDataSource = StoreAPIDataSource;
  }

  async getAll (page: number = 1, limit: number = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE, filters?: StoreListFilters): Promise<{ items: Store[]; pagination?: any }> {
    try {
      const response = await this.storeDataSource.getAll(page, limit, filters);
      return {
        items: response.items.map(mapStoreDTOToModel),
        pagination: response.pagination
      };
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async getNearbyInBounds (bounds: Bounds, includeDetails: boolean = false): Promise<Store[]> {
    try {
      const response = await this.storeDataSource.getNearbyInBounds(bounds, includeDetails);
      return response.map(mapStoreDTOToModel);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async getStoresForMap (bounds?: Bounds, isProvider: boolean = false): Promise<Store[]> {
    try {
      // Use provider-specific endpoint if user is a provider
      if (isProvider && bounds) {
        const response = await ProviderStoreAPIDataSource.getMyNearbyStores(bounds, {
          includeDetails: true,
          limit: 1000
        });
        return response.map(mapStoreDTOToModel);
      }

      // Use regular endpoint for non-providers
      const response = await this.storeDataSource.getStoresForMap(bounds);
      return response.map(mapStoreDTOToModel);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async create (store: Store): Promise<Store> {
    try {
      const storeDTO = mapStoreModelToDTO(store);
      const response = await this.storeDataSource.create(storeDTO);
      return mapStoreDTOToModel(response);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async update (id: string, store: Store): Promise<Store> {
    try {
      const storeDTO = mapStoreModelToDTO(store);
      const response = await this.storeDataSource.update(id, storeDTO);
      return mapStoreDTOToModel(response);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async delete (id: string): Promise<void> {
    try {
      await this.storeDataSource.delete(id);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async addDebt (storeId: string, debtData: { amount: number; description?: string }): Promise<any> {
    try {
      return await this.storeDataSource.addDebt(storeId, debtData);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async getById (id: string): Promise<Store> {
    try {
      const storeDTO = await this.storeDataSource.getById(id);
      return mapStoreDTOToModel(storeDTO);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }

  async getStatistics (): Promise<StoreStatistics> {
    try {
      const response = await this.storeDataSource.getStatistics();
      return mapStoreStatisticsDTOToModel(response);
    } catch (error) {
      throw AppError.fromApiError(error);
    }
  }
}
